import styled from '@emotion/styled';
import Drawer from '@base/Drawer/Drawer';
import H2 from '@base/H2/H2';
import { Spacings, Fonts } from '@styles/index';
import { FC } from 'react';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import { AnyArgVoidReturnFunction } from 'types/utils';
import Image from '@base/Image';

const DrawerHeader = styled(H2)`
  grid-column: span 10;
`;

const DrawerDescription = styled(P1)`
  grid-column: span 10;
  margin-top: ${Spacings.SPACING_4B};
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_5B};
`;

const DrawerContentBlock = styled.div`
  grid-column: span 10;
  display: flex;
  margin: ${Spacings.SPACING_5B} 0;
`;

const DrawerContentHeader = styled(P1)`
  font-size: ${Fonts.LARGE};
  font-weight: ${Fonts.SEMIBOLD};
  margin-bottom: ${Spacings.SPACING_1B};
`;

const DrawerContentDesc = styled(P1)`
  color: var(--color-text-secondary);
`;

const DrawerTextBlock = styled.div`
  margin-left: ${Spacings.SPACING_6B};
`;

const UpgradeIconicText = styled(H4)`
  grid-column: span 10;
  margin: ${Spacings.SPACING_5B} 0;
`;

const FAQFooter = styled.span`
  font-size: ${Fonts.H5};
  color: var(--color-text-primary);
  margin-top: ${Spacings.SPACING_25B};
  margin-bottom: ${Spacings.SPACING_12B};
  grid-column: span 10;
`;

const FAQAction = styled.span`
  font-size: ${Fonts.H5};
  color: var(--color-i-green);
  font-weight: ${Fonts.SEMIBOLD};
  margin-left: ${Spacings.SPACING_2B};
  border-bottom: dashed 1px var(--color-i-green);
  cursor: pointer;
`;

const ImageFlexWrapper = styled.div`
  min-width: 64px;
  height: auto;
`;

type HighlightsDrawerProps = {
  isIconicUpgrade: boolean;
  drawerState: boolean;
  openFAQDrawer: AnyArgVoidReturnFunction;
  closeLearMoreDrawer: AnyArgVoidReturnFunction;
  externalApps: Array<{
    display_name: string;
  }>;
  subscriptionHighlights: any;
};

const HighlightsDrawer: FC<HighlightsDrawerProps> = ({
  isIconicUpgrade,
  drawerState,
  openFAQDrawer,
  closeLearMoreDrawer,
  externalApps,
  subscriptionHighlights
}) => {
  const highlights = isIconicUpgrade
    ? subscriptionHighlights?.upgradeHighlights
    : subscriptionHighlights?.highlights;
  return (
    <Drawer drawerState={drawerState} onClose={closeLearMoreDrawer}>
      <DrawerHeader>{subscriptionHighlights?.header}</DrawerHeader>
      <DrawerDescription>
        {isIconicUpgrade
          ? subscriptionHighlights?.upgradeDesc
          : subscriptionHighlights?.desc}
      </DrawerDescription>
      {isIconicUpgrade && (
        <UpgradeIconicText>
          {`Additional benefits of ${
            externalApps[0]
              ? externalApps[0].display_name
              : 'ICONIC subscription'
          }`}
        </UpgradeIconicText>
      )}
      {highlights
        ? highlights.map((highlight) => {
            return (
              <DrawerContentBlock key={highlight.id}>
                <ImageFlexWrapper>
                  <Image width="64" height="64" src={highlight.logo} alt="-" />
                </ImageFlexWrapper>
                <DrawerTextBlock>
                  <DrawerContentHeader>
                    {highlight.highlightHeader}
                  </DrawerContentHeader>
                  <DrawerContentDesc>{highlight.highlight}</DrawerContentDesc>
                </DrawerTextBlock>
              </DrawerContentBlock>
            );
          })
        : null}
      {isIconicUpgrade && (
        <FAQFooter>
          Have some questions?
          <FAQAction onClick={openFAQDrawer}>Check FAQs</FAQAction>
        </FAQFooter>
      )}
    </Drawer>
  );
};

export default HighlightsDrawer;
